import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import AppProvider from './core/stores/AppProvider';
import LoadingMessageComponent from './components/LoadingMessageComponent';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import SessionService from './core/services/SessionService';

import Header from './layouts/Header';
import Footer from './layouts/Footer';
import ActivityTracker from './components/ActivityTracker';

import routes from './routes';
import './assets/css/app.scss';

/** Must be exactly `true`. Missing/false = activity tracker not mounted. */
const ENABLE_ACTIVITY_LOGGING = process.env.REACT_APP_ENABLE_ACTIVITY_LOGGING === 'true';

// Explicit loaders only — fully dynamic import(`${path}`) makes webpack scan all of
// src (including setupTests → jest-dom → Node `path`/`fs`) and breaks the CRA build.
const pageLoaders = {
	'./features/site/pages/get-quote/': () => import('./features/site/pages/get-quote/'),
	'./features/site/pages/select-premium/': () => import('./features/site/pages/select-premium/'),
	'./features/site/pages/animal-details/': () => import('./features/site/pages/animal-details/'),
	'./features/site/pages/policy-holder-details/': () => import('./features/site/pages/policy-holder-details/'),
	'./features/site/pages/preview/': () => import('./features/site/pages/preview/'),
	'./features/site/pages/price/': () => import('./features/site/pages/price/'),
	'./features/site/pages/quoteDetails/': () => import('./features/site/pages/quoteDetails/'),
	'./features/site/pages/retrieve-quote/': () => import('./features/site/pages/retrieve-quote/'),
};

function App() {
  // Initialize session when app starts
  useEffect(() => {
    SessionService.initializeSession().catch(error => {
      console.error('Failed to initialize session on app startup:', error);
    });
  }, []);

  return (
    <ErrorBoundary>
      <AppProvider>
          <BrowserRouter basename='/'>
              {ENABLE_ACTIVITY_LOGGING && <ActivityTracker />}
              <Header />
              <div className='wrapper'>
                  <div id='content' className='container-fluid'>
                      <Suspense fallback={<LoadingMessageComponent />}>
                          <Routes>
                              {routes.map(route => {
                                  const {componentPath, title, path, requiresAuth = true} = route;
                                  const loadPage = pageLoaders[componentPath];
                                  if (!loadPage) {
                                      console.error(`No page loader registered for ${componentPath}`);
                                      return null;
                                  }
                                  const Component = lazy(
                                      () => {
                                          document.title = title;
                                          return loadPage();
                                      }
                                  );

                                  return (
                                      <Route
                                          key={path}
                                          path={path}
                                          element={
                                              <ProtectedRoute requiresAuth={requiresAuth}>
                                                  <Component />
                                              </ProtectedRoute>
                                          }
                                      />
                                  );
                              })};
                          </Routes>
                      </Suspense>
                  </div>
              </div>
              <Footer/>
          </BrowserRouter>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
